/* Luova.fi collection listing: filter rail / mobile filter drawer, sort, pagination.
   Changes re-render the product-grid section in place through the Section Rendering API
   and update the URL, so back/forward and shared links work. Without JavaScript the
   filter form and the page links still work as plain page loads. */
(() => {
  if (customElements.get('luova-listing-ready')) return;
  customElements.define('luova-listing-ready', class extends HTMLElement {});

  /* Parts of the section replaced after each fetch. The rail keeps its open/closed drawer
     state and scroll position because only its contents are swapped. */
  const PARTS = ['[data-luova-filter-groups]', '[data-luova-filters-clear]', '[data-luova-results]', '[data-luova-filters-apply]'];

  const expanded = new Set();
  let controller = null;

  function paramsFromForm(listing) {
    const form = listing.querySelector('[data-luova-filter-form]');
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const sort = listing.querySelector('[data-luova-sort]');
    if (sort) params.set('sort_by', sort.value);
    return params;
  }

  function render(listing, url, push = true) {
    const sectionId = listing.dataset.sectionId;
    const target = new URL(url, location.origin);
    target.searchParams.delete('section_id');
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', sectionId);

    if (controller) controller.abort();
    controller = new AbortController();
    listing.classList.add('is-loading');
    const focusedId = document.activeElement && listing.contains(document.activeElement) ? document.activeElement.id : null;

    fetch(fetchUrl, { signal: controller.signal })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-luova-listing]');
        if (!fresh) {
          location.href = target;
          return;
        }
        PARTS.forEach((sel) => {
          const from = fresh.querySelector(sel);
          const to = listing.querySelector(sel);
          if (from && to) to.replaceWith(from);
        });
        const sortInput = listing.querySelector('[data-luova-filter-form] input[name="sort_by"]');
        const sortSelect = listing.querySelector('[data-luova-sort]');
        if (sortInput && sortSelect) sortInput.value = sortSelect.value;
        restoreExpanded(listing);
        if (push) history.pushState({ luovaListing: true }, '', target);
        if (focusedId) {
          const again = document.getElementById(focusedId);
          if (again) again.focus({ preventScroll: true });
        }
      })
      .catch((e) => {
        if (e.name !== 'AbortError') location.href = target;
      })
      .finally(() => listing.classList.remove('is-loading'));
  }

  function restoreExpanded(listing) {
    listing.querySelectorAll('[data-luova-filter-group]').forEach((group) => {
      const open = expanded.has(group.dataset.luovaFilterGroup);
      group.classList.toggle('is-expanded', open);
      const btn = group.querySelector('[data-luova-filter-toggle]');
      if (btn) {
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      }
    });
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function init(listing) {
    if (listing.dataset.luovaReady) return;
    listing.dataset.luovaReady = 'true';
    const base = () => listing.querySelector('[data-luova-filter-form]').action;

    const apply = () => {
      const params = paramsFromForm(listing);
      const url = new URL(base(), location.origin);
      url.search = params.toString();
      render(listing, url.toString());
    };
    const applySoon = debounce(apply, 500);

    /* Delegated: the parts inside are replaced after every render. */
    listing.addEventListener('change', (e) => {
      if (e.target.matches('[data-luova-sort]')) {
        apply();
      } else if (e.target.closest('[data-luova-filter-form]')) {
        if (e.target.type === 'number') applySoon();
        else apply();
      }
    });
    listing.addEventListener('input', (e) => {
      if (e.target.type === 'number' && e.target.closest('[data-luova-filter-form]')) applySoon();
    });
    listing.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-luova-filter-form]')) return;
      e.preventDefault();
      apply();
    });

    listing.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-luova-page-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        render(listing, link.href);
        const results = listing.querySelector('[data-luova-results]');
        if (link.closest('.luova-pagination') && results) {
          results.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
        return;
      }

      // Search page: "Näytä lisää" reveals the next 24 hits of its block.
      const more = e.target.closest('[data-luova-more]');
      if (more) {
        const group = more.closest('.luova-search-page__group');
        const next = [...group.querySelectorAll('[data-luova-more-item][hidden]')].slice(0, 24);
        next.forEach((item) => (item.hidden = false));
        if (!group.querySelector('[data-luova-more-item][hidden]')) {
          next[0]?.querySelector('a')?.focus({ preventScroll: true });
          more.remove();
        }
        return;
      }

      const toggle = e.target.closest('[data-luova-filter-toggle]');
      if (toggle) {
        const group = toggle.closest('[data-luova-filter-group]');
        const key = group.dataset.luovaFilterGroup;
        if (expanded.has(key)) expanded.delete(key);
        else expanded.add(key);
        restoreExpanded(listing);
        return;
      }

      if (e.target.closest('[data-luova-filters-open]')) openDrawer(listing, e.target.closest('[data-luova-filters-open]'));
      else if (e.target.closest('[data-luova-filters-close]')) closeDrawer(listing);
    });

    listing.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && listing.querySelector('[data-luova-filters-panel]').classList.contains('is-open')) {
        closeDrawer(listing);
      }
    });

    window.addEventListener('popstate', () => render(listing, location.href, false));
    window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
      if (e.matches) closeDrawer(listing);
    });
  }

  /* Mobile filter drawer */
  let opener = null;
  function openDrawer(listing, trigger) {
    const panel = listing.querySelector('[data-luova-filters-panel]');
    opener = trigger;
    panel.classList.add('is-open');
    listing.querySelectorAll('[data-luova-filters-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('overflow-hidden');
    const sheet = panel.querySelector('.luova-filters__sheet');
    if (typeof trapFocus === 'function') trapFocus(sheet, sheet.querySelector('button, input, a'));
  }

  function closeDrawer(listing) {
    const panel = listing.querySelector('[data-luova-filters-panel]');
    if (!panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    listing.querySelectorAll('[data-luova-filters-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(opener);
  }

  const start = () => document.querySelectorAll('[data-luova-listing]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
